import type { Entry, EntryMedia, MediaKind } from "../desk-records";

export function mediaKindOf(path: string): MediaKind {
  const lower = path.toLowerCase();

  if (lower.endsWith(".gif")) return "gif";

  if (/\.(mp4|mov|m4v|webm)$/.test(lower)) return "video";

  return "image";
}

/** `/api/observatory/v2/policy-files/{id}/entry/{path}`: same origin, so the session cookie rides along. */
export function entryMemberUrl({
  policyVersionId,
  path,
}: {
  readonly policyVersionId: string;
  readonly path: string;
}): string {
  const encoded = path.split("/").map(encodeURIComponent).join("/");
  return `/api/observatory/v2/policy-files/${encodeURIComponent(policyVersionId)}/entry/${encoded}`;
}

/**
 * `entry.json` in either schema, as the desk reads it. v1 carried one `media`
 * path and a top-level `alt_text`; v2 carries a list. Anything else is text.
 */
export function normaliseEntry(raw: unknown): Entry | null {
  if (!raw || typeof raw !== "object") return null;

  const textValue = "text" in raw ? raw.text : undefined;
  const post = "post" in raw ? raw.post : undefined;
  const text =
    typeof textValue === "string"
      ? textValue
      : typeof post === "string"
        ? post
        : "";
  return {
    schema: "schema" in raw && typeof raw.schema === "string" ? raw.schema : "",
    text,
    media: mediaFromEntry(raw),
  };
}

function mediaFromEntry(doc: object): EntryMedia[] {
  const media = "media" in doc ? doc.media : undefined;

  if (Array.isArray(media)) {
    return media.flatMap((item: unknown) => {
      if (typeof item === "string") return [{ path: item, alt_text: "" }];

      if (
        !item ||
        typeof item !== "object" ||
        !("path" in item) ||
        typeof item.path !== "string"
      )
        return [];

      const altText = "alt_text" in item ? item.alt_text : null;
      return [
        {
          path: item.path,
          alt_text: typeof altText === "string" ? altText : "",
        },
      ];
    });
  }

  const video = "video" in doc ? doc.video : undefined;
  const path =
    typeof media === "string" && media
      ? media
      : typeof video === "string"
        ? video
        : null;

  if (!path) return [];

  const altText = "alt_text" in doc ? doc.alt_text : undefined;
  return [{ path, alt_text: typeof altText === "string" ? altText : "" }];
}
